import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  subscribeToUserNotifications,
  updateNotificationAsRead,
} from "config/firebaseConfig";
import { useClickOutside } from "../hooks/useClickOutside";
import { resolveNotificationTarget } from "utils/notificationRouting";
import { useEnrichRequestAnsweredNotifications } from "../hooks/useEnrichRequestAnsweredNotifications";
import { buildNotificationMetaLine, stripFlightMention } from "utils/notificationText";

const DROPDOWN_PREVIEW_COUNT = 5;

interface NotificationItem {
  id: string;
  title: string;
  message: string;
  responderName: string;
  time: string;
  isNew: boolean;
  type?: string;
  requestDetailId?: number | null;
  // Not written by the backend yet — see notif.flightNumber mapping below.
  flightNumber?: string;
}

interface NotificationDropdownProps {
  icon: string;
  buttonAltText?: string;
  collectionName: "load_notifications" | "admin_notifications";
}

const AnsweredIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 6C4 4.89543 4.89543 4 6 4H18C19.1046 4 20 4.89543 20 6V13C20 14.1046 19.1046 15 18 15H9.41421C9.1491 15 8.89482 15.1054 8.70711 15.2929L5.70711 18.2929C5.4741 18.5259 5 18.3611 5 18.0303V15.5C5 15.2239 4.77614 15 4.5 15C4.22386 15 4 14.7761 4 14.5V6Z"
      stroke="#22C55E"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="16" cy="7.5" r="4.5" fill="#22C55E" />
    <path
      d="M14.2 7.5L15.3 8.6L17.8 6.1"
      stroke="white"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CancelledIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 6C4 4.89543 4.89543 4 6 4H18C19.1046 4 20 4.89543 20 6V13C20 14.1046 19.1046 15 18 15H9.41421C9.1491 15 8.89482 15.1054 8.70711 15.2929L5.70711 18.2929C5.4741 18.5259 5 18.3611 5 18.0303V15.5C5 15.2239 4.77614 15 4.5 15C4.22386 15 4 14.7761 4 14.5V6Z"
      stroke="#EF4444"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="16" cy="7.5" r="4.5" fill="#EF4444" />
    <path
      d="M14.2 7.5H17.8"
      stroke="white"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

const isCancelledNotification = (title: string, message: string) => {
  const combined = `${title} ${message}`.toLowerCase();
  return (
    combined.includes("cancel") ||
    combined.includes("reject") ||
    combined.includes("remove") ||
    combined.includes("decline") ||
    combined.includes("fail")
  );
};

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  icon,
  buttonAltText = "Notifications",
  collectionName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const unreadCount = notifications.filter((n) => n.isNew).length;
  const previewNotifications = notifications.slice(0, DROPDOWN_PREVIEW_COUNT);

  useEnrichRequestAnsweredNotifications(notifications, setNotifications);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setNotifications([]);
        return;
      }

      const unsubscribeNotifications = subscribeToUserNotifications(
        user.uid,
        (fetched) => {
          const mapped: NotificationItem[] = fetched
            .map((notif: any) => ({
              id: notif.id,
              title: notif.title || "",
              responderName: notif.responderName || "Anonymous",
              message: notif.message || "",
              time: notif.createdAt?.toDate?.().toISOString() || "",
              isNew: !notif.IsRead,
              type: notif.type,
              requestDetailId: notif.requestDetailId ?? null,
              flightNumber: notif.flightNumber || "",
            }))
            .sort(
              (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime(),
            );
          setNotifications(mapped);
        },
        collectionName,
      );

      return () => unsubscribeNotifications();
    });

    return () => unsubscribeAuth();
  }, [collectionName]);

  useClickOutside(dropdownRef, () => setIsOpen(false), true, "pointerdown");

  const handleNotificationClick = async (notification: NotificationItem) => {
    // Mark-as-read is fire-and-forget: it must not delay navigation, otherwise
    // the first click on an unread item just sits there for the Firestore
    // round trip and looks like it did nothing.
    if (notification.isNew) {
      const auth = getAuth();
      if (auth.currentUser) {
        updateNotificationAsRead(
          notification.id,
          auth.currentUser.uid,
          collectionName,
        );
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id ? { ...n, isNew: false } : n,
          ),
        );
      }
    }
    setIsOpen(false);
    const target = await resolveNotificationTarget(notification);
    navigate(target ? target.path : "/notifications", target?.state ? { state: target.state } : undefined);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-12 h-12 max-h-12 rounded-[18px] border border-gray-200/80 bg-white hover:bg-gray-50 flex items-center justify-center transition-colors focus:outline-none relative shrink-0 cursor-pointer shadow-xs"
        title={buttonAltText}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-gray-900"
        >
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-[#EF4444] text-white text-[11px] font-bold rounded-full w-5 h-5 flex items-center justify-center border-2 border-white shadow-xs">
            {unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        // top-16 alone is a fixed 64px offset from the true viewport top — it
        // doesn't account for the giveaway banner's height pushing the actual
        // header down, so with the banner showing it overlapped the header
        // instead of sitting below it. header-dropdown-mobile-top (below sm)
        // accounts for that; sm:top-full is unaffected since it's positioned
        // relative to the trigger itself, not the viewport, and an inline
        // style would have outranked it, so this is a real class — see
        // HeaderFavouritesDropdown.tsx for the same fix.
        <div className="fixed left-3 right-3 header-dropdown-mobile-top z-50 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-1 sm:translate-x-40 w-[calc(100vw-24px)] sm:w-[300px] bg-white rounded-[14px] shadow-2xl border border-gray-100/80 overflow-hidden text-left">
          {/* Header */}
          <div className="bg-[#192227] px-4 py-2.5 flex items-center gap-1.5">
            <h3 className="text-white font-semibold text-[14px] tracking-tight">
              Notifications
            </h3>
            {unreadCount > 0 && (
              <span className="w-4 h-4 rounded-full bg-white text-[#192227] text-[10px] font-bold flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </div>

          {/* List */}
          <div className="flex flex-col px-3 py-2 max-h-[380px] overflow-y-auto">
            {previewNotifications.length > 0 ? (
              previewNotifications.map((notification, index) => {
                const isCancelled = isCancelledNotification(
                  notification.title,
                  notification.message,
                );
                const metaLine = buildNotificationMetaLine(
                  notification.flightNumber,
                  notification.time,
                );
                return (
                  <div key={notification.id}>
                    {index > 0 && (
                      <div className="h-px w-full bg-gray-100 my-1" />
                    )}
                    <div
                      onClick={() => handleNotificationClick(notification)}
                      className={`flex items-start gap-2.5 px-2 py-1 rounded-lg transition-colors cursor-pointer ${
                        notification.isNew
                          ? "bg-[#FFF4D4] hover:bg-[#fee9ad]"
                          : "hover:bg-gray-50/80"
                      }`}
                    >
                      <div
                        className={`w-8 h-8 rounded shrink-0 border-[0.2px] flex items-center justify-center ${
                          isCancelled
                            ? "bg-[rgba(220,64,67,0.06)] border-[#dc4043]"
                            : "bg-[rgba(73,168,93,0.04)] border-[#49a85d]"
                        }`}
                      >
                        <div className="w-[22px] h-[22px] flex items-center justify-center">
                          {isCancelled ? <CancelledIcon /> : <AnsweredIcon />}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[13px] font-semibold text-[#192227] leading-tight">
                            {notification.title ||
                              (isCancelled
                                ? "Request Was Cancelled"
                                : "Request Answered")}
                          </span>
                          {notification.isNew && (
                            <span className="w-[6px] h-[6px] rounded-full bg-[#FFC419] shrink-0 inline-block" />
                          )}
                        </div>
                        <p className="text-[11px] text-[#5e5e5e] font-normal mt-0.5 leading-snug">
                          {stripFlightMention(
                            notification.message ||
                              (isCancelled
                                ? "Your flight load request has been cancelled."
                                : "Your flight load request has been answered."),
                            notification.flightNumber,
                          )}
                        </p>
                        {metaLine && (
                          <p className="text-[10px] text-[#9CA3AF] font-normal mt-1">
                            {metaLine}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex items-center justify-center py-8">
                <p className="text-gray-400 text-sm font-medium">
                  No notifications
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 pb-3 pt-0.5 bg-white">
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                navigate("/notifications");
              }}
              className="text-[13px] font-medium text-black hover:text-gray-700 inline-flex items-center gap-1 border-b border-dashed border-black pb-0.5 transition-all"
            >
              View all Notifications &gt;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
